import React, { useMemo } from "react";
import styled, { useTheme } from "styled-components";
import IconWithFallback from "Global/IconWithFallback";
import { ICON_TYPES } from "@shared/common/constants";
import { SingleValueChartData } from "types/common";
import useGraphDataSocket from "@shared/services/socket/useGraphDataSocket";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { TIME_RANGE_1_DAY } from "common/constants";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";

const compactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 2,
});

/** "$1.2M", "₿340.5"; a sliver that would read "₿0" shows "<₿0.01". */
export const formatCompactAmount = (value: number, currency: string) =>
  value > 0 && value < 0.005
    ? `<${currency}0.01`
    : `${currency}${compactFormatter.format(value)}`;

const formatChange = (change: number) =>
  `${change > 0 ? "+" : change < 0 ? "−" : ""}${Math.abs(change).toFixed(2)}%`;

/** The pair as on the Dashboard: coin icon, base in ink, quote muted. */
export const NewLookPair = ({ symbol }: { symbol: string }) => {
  const [base, quote] = symbol.split("/");
  return (
    <Pair>
      <IconWithFallback
        coinOrExchangeName={symbol}
        iconType={ICON_TYPES.COIN}
        pxIconSize={24}
      />
      <PairName>
        {base}
        {quote && <Quote>/{quote}</Quote>}
      </PairName>
    </Pair>
  );
};

interface NewLookLiveChangeProps {
  symbol: string;
  exchange: string;
  /** 24h change as a fraction (0.0123 = 1.23%). */
  initialChange: number;
}

/** The live 24h change as coloured text (no pill). */
export const NewLookLiveChange = ({
  symbol,
  exchange,
  initialChange,
}: NewLookLiveChangeProps) => {
  const tokens = getNewLookTokens(useTheme());
  const { change: socketChange } = useGraphDataSocket(symbol, exchange);
  const change = Number(socketChange ?? initialChange ?? 0) * 100;
  return (
    <Figure style={{ color: directionColor(tokens, change) }}>
      {formatChange(change)}
    </Figure>
  );
};

interface NewLookRangeProps {
  graphData?: SingleValueChartData;
  low?: number | null;
  high?: number | null;
  currency: string;
  precision: number;
}

/**
 * Where the price sits in its 24h range: a slim track filled to the price in
 * the day's direction, with the low and high under it (the Dashboard's
 * markets list).
 */
export const NewLookRange = ({
  graphData,
  low,
  high,
  currency,
  precision,
}: NewLookRangeProps) => {
  const tokens = getNewLookTokens(useTheme());
  const { position, change } = useMemo(() => {
    const series = (graphData?.[TIME_RANGE_1_DAY] || [])
      .map((point) => Number(point[1]))
      .filter((value) => Number.isFinite(value));
    if (series.length < 2) return { position: undefined, change: 0 };
    const min = Math.min(...series);
    const max = Math.max(...series);
    return {
      position:
        max > min ? (series[series.length - 1] - min) / (max - min) : undefined,
      change: series[series.length - 1] - series[0],
    };
  }, [graphData]);
  const label = (value?: number | null) =>
    value !== null && value !== undefined
      ? `${currency}${getLocaleStringWithPrecision(value, precision)}`
      : "—";

  return (
    <Range>
      <Track>
        {position !== undefined && (
          <>
            <Fill
              style={{
                width: `${position * 100}%`,
                background: directionColor(tokens, change),
              }}
            />
            <Marker style={{ left: `${position * 100}%` }} />
          </>
        )}
      </Track>
      <RangeLabels>
        <span>{label(low)}</span>
        <span>{label(high)}</span>
      </RangeLabels>
    </Range>
  );
};

const Pair = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;
const PairName = styled.span`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const Quote = styled.span`
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Figure = styled.span`
  ${newLookType.figure};
  font-size: 14px;
  white-space: nowrap;
`;
const Range = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;
`;
const Track = styled.div`
  position: relative;
  height: 4px;
  border-radius: 2px;
  background: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
`;
const Fill = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-radius: 2px;
  opacity: 0.35;
`;
const Marker = styled.span`
  position: absolute;
  top: 50%;
  width: 8px;
  height: 8px;
  margin: -4px 0 0 -4px;
  border-radius: 50%;
  background: ${(p) => getNewLookTokens(p.theme).ink};
  box-shadow: 0 0 0 2px ${(p) => getNewLookTokens(p.theme).panel};
`;
const RangeLabels = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  ${newLookType.meta};
  font-size: 11px;
  line-height: 14px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
